import { authenticatedFetch } from "./api/client";
import { authenticatedProApiFetch } from "./api/pro-api.client";

const ALLOWED_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

export interface AvatarUploadUrlResponse {
  uploadUrl: string;
  avatarUrl: string;
  key: string;
  contentType: string;
  expiresInSeconds: number;
}

function normalizeContentType(file: File): string {
  const type = file.type?.trim().toLowerCase();
  if (type && ALLOWED_TYPES.has(type)) return type;
  const name = file.name.toLowerCase();
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".webp")) return "image/webp";
  if (name.endsWith(".gif")) return "image/gif";
  return "image/jpeg";
}

export async function requestAvatarUploadUrl(
  accessToken: string,
  contentType: string,
): Promise<AvatarUploadUrlResponse> {
  return authenticatedFetch<AvatarUploadUrlResponse>(
    "/users/me/avatar/upload-url",
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ contentType }),
    },
  );
}

export async function uploadAvatarFile(
  accessToken: string,
  file: File,
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error("Image must be 5 MB or smaller.");
  }

  const contentType = normalizeContentType(file);
  const instructions = await requestAvatarUploadUrl(accessToken, contentType);

  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");

  const headers: Record<string, string> = {
    "Content-Type": contentType,
  };
  if (!isPresigned) {
    if (instructions.key) {
      headers["X-Avatar-Key"] = instructions.key;
    }
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  const uploadResponse = await fetch(instructions.uploadUrl, {
    method: "PUT",
    body: file,
    headers,
  });

  if (!uploadResponse.ok) {
    let message = "Failed to upload image";
    try {
      const errBody = (await uploadResponse.json()) as { error?: string };
      if (errBody.error) message = errBody.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  if (uploadResponse.headers.get("content-type")?.includes("application/json")) {
    const body = (await uploadResponse.json()) as { avatarUrl?: string };
    if (body.avatarUrl) return body.avatarUrl;
  }

  return instructions.avatarUrl;
}

/**
 * Ask the backend to download a remote image URL and store a hosted copy.
 * Returns the hosted avatar URL (S3/local).
 */
export async function uploadAvatarFromUrl(
  accessToken: string,
  url: string,
): Promise<string> {
  const result = await authenticatedFetch<{ avatarUrl: string }>(
    "/users/me/avatar/from-url",
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ url }),
    },
  );
  return result.avatarUrl;
}

// ─── Product images ───────────────────────────────────────────────────────────

export interface ProductImage {
  id: string;
  url: string;
  thumbUrl?: string | null;
  cardUrl?: string | null;
  sortOrder: number;
}

interface ProductImageUploadInstructions {
  uploadUrl: string;
  imageUrl: string;
  key: string;
  contentType: string;
  expiresInSeconds: number;
}

export async function uploadProductImageFile(
  accessToken: string,
  productId: string,
  file: File,
): Promise<{ url: string; id: string }> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Image must be 10 MB or smaller.");
  }

  const contentType = normalizeContentType(file);
  const instructions = await authenticatedProApiFetch<ProductImageUploadInstructions>(
    `/products/${productId}/images/upload-url`,
    accessToken,
    { method: "POST", body: JSON.stringify({ contentType }) },
  );

  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": contentType };
  if (!isPresigned) {
    if (instructions.key) headers["X-Image-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }

  const uploadResponse = await fetch(instructions.uploadUrl, { method: "PUT", body: file, headers });
  if (!uploadResponse.ok) {
    let message = "Failed to upload image";
    try {
      const errBody = (await uploadResponse.json()) as { error?: string };
      if (errBody.error) message = errBody.error;
    } catch { /* ignore */ }
    throw new Error(message);
  }

  const body = (await uploadResponse.json()) as { id: string; url: string; sortOrder: number };
  return { url: body.url, id: body.id };
}

/**
 * Ask the backend to download a remote image URL and store a hosted product image.
 * Returns the hosted image id + URL (same shape as uploadProductImageFile).
 */
export async function uploadProductImageFromUrl(
  accessToken: string,
  productId: string,
  url: string,
): Promise<{ url: string; id: string }> {
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    throw new Error("Enter a valid image URL (http or https).");
  }
  if (trimmed.length > 2048) {
    throw new Error("Image URL is too long.");
  }

  const result = await authenticatedProApiFetch<{
    id: string;
    url: string;
    thumbUrl?: string;
    sortOrder: number;
  }>(`/products/${productId}/images/from-url`, accessToken, {
    method: "POST",
    body: JSON.stringify({ url: trimmed }),
  });

  return { url: result.thumbUrl ?? result.url, id: result.id };
}

export async function fetchProductImages(
  accessToken: string,
  productId: string,
): Promise<ProductImage[]> {
  try {
    const result = await authenticatedProApiFetch<{ images: ProductImage[] }>(
      `/products/${productId}/images`,
      accessToken,
    );
    return result.images ?? [];
  } catch {
    return [];
  }
}

export async function deleteProductImage(
  accessToken: string,
  productId: string,
  imageId: string,
): Promise<void> {
  await authenticatedProApiFetch<void>(
    `/products/${productId}/images/${imageId}`,
    accessToken,
    { method: "DELETE" },
  );
}

export async function reorderProductImages(
  accessToken: string,
  productId: string,
  imageIds: string[],
): Promise<void> {
  await authenticatedProApiFetch<void>(
    `/products/${productId}/images/reorder`,
    accessToken,
    { method: "PATCH", body: JSON.stringify({ ids: imageIds }) },
  );
}

// ─── Organization logo ────────────────────────────────────────────────────────

/**
 * Upload an organization logo file.
 * Uses a separate endpoint from user avatars.
 */
export async function uploadOrgLogoFile(
  accessToken: string,
  file: File,
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  if (file.size > 5 * 1024 * 1024) {
    throw new Error("Image must be 5 MB or smaller.");
  }

  const contentType = normalizeContentType(file);
  const instructions = await authenticatedFetch<AvatarUploadUrlResponse>(
    "/organizations/logo/upload-url",
    accessToken,
    {
      method: "POST",
      body: JSON.stringify({ contentType }),
    },
  );

  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");

  const headers: Record<string, string> = {
    "Content-Type": contentType,
  };
  if (!isPresigned) {
    if (instructions.key) {
      headers["X-Avatar-Key"] = instructions.key;
    }
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
  }

  const uploadResponse = await fetch(instructions.uploadUrl, {
    method: "PUT",
    body: file,
    headers,
  });

  if (!uploadResponse.ok) {
    let message = "Failed to upload image";
    try {
      const errBody = (await uploadResponse.json()) as { error?: string };
      if (errBody.error) message = errBody.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }

  if (uploadResponse.headers.get("content-type")?.includes("application/json")) {
    const body = (await uploadResponse.json()) as { avatarUrl?: string };
    if (body.avatarUrl) return body.avatarUrl;
  }

  return instructions.avatarUrl;
}
